const _Swiper = typeof Swiper === 'function' ? Swiper : null

const canonicalImageSource = (source) => {
    const url = new URL(source, window.location.href)
    const path = url.pathname.replace(/_(?:\d+x\d*|x\d+|master)(?=\.[^/.]+$)/i, '')
    const version = url.searchParams.get('v')
    return { path: `${url.origin}${path}`, version }
}

const findMatchingSlide = (slides, featuredSrc) => {
    return slides.find(slide => {
        const img = slide.querySelector('img');

        if (img) {
            // data-zoom is the gallery's canonical master URL. Fall back to the
            // rendered source for cards that do not use lazy-img.
            const slideSource = img.dataset.zoom || img.currentSrc || img.src
            const slideImage = canonicalImageSource(slideSource)
            const featuredImage = canonicalImageSource(featuredSrc)

            return slideImage.path === featuredImage.path &&
                (!slideImage.version || !featuredImage.version || slideImage.version === featuredImage.version)
        }
        return false
    });
};

function standardizeString(str) {
    return str
        .toLowerCase()
        .replace(/\|.*/, "")
        .replace(/\(.*?\)/g, "")
        .trim();
}

class productMediaNew extends HTMLElement {
    constructor() {
        super()
        this.boundVariantUpdated = this.handleVariantUpdated.bind(this)
        this.initConfigs();
        this.thumbnailPurgatory = this.queryElement('[data-product-media-new-thumbnail-purgatory]');
        this.mediaPurgatory = this.queryElement('[data-product-media-new-media-purgatory]');
        this.initSwiper()
    }

    queryElement(selector) {
        return this.querySelector(selector);
    }

    initConfigs() {

        // Thumbnails Swiper config
        this.thumbnailConfig = {
            direction: 'vertical',
            slidesPerView: 6,
            spaceBetween: 20,
            watchSlidesVisibility: true,
            autoHeight: true,
            navigation: {
                nextEl: '[data-product-media-new-thumbnail-swiper-next]',
                prevEl: '[data-product-media-new-thumbnail-swiper-prev]',
            }
        }

        // Main Swiper config
        this.mainConfig = {
            slidesPerView: 'auto',
            spaceBetween: 1, // this needs to be 1 or the next image peeks through for some reason
            loop: false,
            pagination: {
                el: '[data-product-media-swiper-pagination]',
                clickable: true,
                type: 'custom',
                renderCustom(_, current, total) {
                    return (`
                              ${current}/${total}
                          `)
                }
            },
            navigation: {
                nextEl: '[data-product-media-new-swiper-next]',
                prevEl: '[data-product-media-new-swiper-prev]',
            },
            breakpoints: {
                750: {
                    pagination: {
                        el: null
                    },
                    navigation: {
                        nextEl: null,
                        prevEl: null
                    }
                }
            },
        }
    }

    connectedCallback() {
        // A Shopify section can be detached and reattached by the theme editor.
        // Recreate instances after disconnect cleanup, but never initialize the
        // same gallery twice.
        if (!this.swiperInstance && _Swiper) this.initSwiper()
        this.initListeners()
    }

    disconnectedCallback() {
        this.removeListeners()
        this.destroySwipers()
    }

    initSwiper() {
        if (!_Swiper) return
        const thumbnailElement = this.queryElement('[data-product-media-new-thumbnail-swiper]')
        const mainElement = this.queryElement('[data-product-media-new-swiper]')
        if (!mainElement) return
        if (thumbnailElement) {
            this.thumbnailSwiperInstance = new _Swiper(thumbnailElement, this.thumbnailConfig)
            this.mainConfig.thumbs = { swiper: this.thumbnailSwiperInstance }
        } else {
            // Product pages with one image have no thumbnail track. Swiper can
            // still provide the main gallery/pagination without a thumbs link.
            delete this.mainConfig.thumbs
        }
        this.swiperInstance = new _Swiper(mainElement, this.mainConfig)
    }

    destroySwipers() {
        if (this.swiperInstance && !this.swiperInstance.destroyed) {
            this.swiperInstance.destroy(true, true)
        }
        if (this.thumbnailSwiperInstance && !this.thumbnailSwiperInstance.destroyed) {
            this.thumbnailSwiperInstance.destroy(true, true)
        }
        this.swiperInstance = null
        this.thumbnailSwiperInstance = null
    }

    removeListeners() {
        if (this.variantListenerAttached) {
            window.removeEventListener('variantUpdated', this.boundVariantUpdated)
            this.variantListenerAttached = false
        }
    }

    handleVariantUpdated(event) {
        const mainElement = this.queryElement('[data-product-media-new-swiper]')
        if (!mainElement || !this.swiperInstance) return
        const hasSwatchesText = mainElement.dataset.productHasSwatches || 'false';
        const hasSwatches = hasSwatchesText.trim().toLowerCase() === 'true';
        if (hasSwatches) {
            this.handleSwatches(event);
        } else {
            this.handleNoSwatches(event);
        }
    }

    handleSwatches(event) {
        if (!this.swiperInstance || !this.thumbnailSwiperInstance || !this.mediaPurgatory || !this.thumbnailPurgatory) return
        const variant = event.detail.variant

        // Get all slides, including those in the purgatory
        const allSlides = Array.from(this.mediaPurgatory.children).concat(Array.from(this.swiperInstance.slides))
        const allThumbnails = Array.from(this.thumbnailPurgatory.children).concat(Array.from(this.thumbnailSwiperInstance.slides))
        
        allSlides.sort((a, b) => {{
           return +a.dataset.index - +b.dataset.index;
        }})

        allThumbnails.sort((a, b) => {{
              return +a.dataset.index - +b.dataset.index;
        }})

        const matchingSlides = allSlides.filter(slide => {
            const img = slide.querySelector('img')
            if (!img) { return false }

            const replacedVariantTitle = standardizeString(variant.title)
            const trimmedAltTag = standardizeString(img.alt || 'noAlt')

            if (trimmedAltTag === 'all') {
                return true
            }

            return img && trimmedAltTag === replacedVariantTitle
        })

        const validIndexes = matchingSlides.length > 0 ?
            matchingSlides.map(slide => slide.dataset.mediaPosition) : allSlides.map(slide => slide.dataset.mediaPosition)

        allThumbnails.forEach(thumbnail => {
            const index = thumbnail.dataset.mediaPosition
            if (validIndexes.includes(index)) {
                this.thumbnailSwiperInstance.el.querySelector('.swiper-wrapper').appendChild(thumbnail)

            } else {
                this.thumbnailPurgatory.appendChild(thumbnail)
            }
        })

        allSlides.forEach(slide => {
            const index = slide.dataset.mediaPosition
            if (validIndexes.includes(index)) {
                this.swiperInstance.el.querySelector('.swiper-wrapper').appendChild(slide)  
            } else {
                this.mediaPurgatory.appendChild(slide)

            }
        })


        this.swiperInstance.update()
        this.thumbnailSwiperInstance.update()
        this.swiperInstance.slideTo(0)
    }

    handleNoSwatches(event) {
        if (!this.swiperInstance) return
        const variant = event.detail.variant;
        const slides = Array.from(this.swiperInstance.slides);

        if (variant && variant.featured_image && variant.featured_image.src) {
            const featuredSrc = variant.featured_image.src;
            const matchingSlide = findMatchingSlide(slides, featuredSrc);

            if (matchingSlide) {
                const index = slides.indexOf(matchingSlide);
                this.swiperGoTo(index);
            }
        }
    }

    swiperGoTo(index) {
        this.swiperInstance.slideTo(index);
    }

    initListeners() {
        if (this.variantListenerAttached) return
        window.addEventListener('variantUpdated', this.boundVariantUpdated)
        this.variantListenerAttached = true
    }
}

customElements.define('product-media-new', productMediaNew)
